import React, { useEffect, useState } from 'react';
import { ScanFace } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Select from '../ui/Select';
import { faceMatchingService, MatchingProvider, MatchingStatus, matchingError } from '../../services/face-matching.service';

const FaceMatchingProvider: React.FC = () => {
  const [status, setStatus] = useState<MatchingStatus | null>(null);
  const [selected, setSelected] = useState<MatchingProvider>('local');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    faceMatchingService.status().then(result => {
      if (active) { setStatus(result); setSelected(result.provider || 'local'); }
    }).catch(error => { if (active) setError(matchingError(error)); });
    return () => { active = false; };
  }, []);

  const save = async () => {
    if (!status) return;
    setBusy(true); setError(''); setSaved(false);
    try {
      const result = await faceMatchingService.settings({ enabled: status.enabled, provider: selected });
      if (result.provider !== selected) throw new Error('The API did not apply the provider selection. Deploy the updated API and retry.');
      setStatus(result); setSelected(result.provider || 'local'); setSaved(true);
    } catch (error) { setError(matchingError(error)); }
    finally { setBusy(false); }
  };

  return <Card padding="md">
    <div className="flex items-start gap-3">
      <ScanFace size={24} className="mt-1 shrink-0 text-accent-primary" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-3">
        <h2 className="text-lg font-semibold text-text-primary">Face matching</h2>
        <p className="text-sm text-text-muted">Choose how this company matches event snapshots with its Person Registry.</p>
        {!status && !error && <p className="text-sm text-text-muted">Loading face-matching settings…</p>}
        {status && <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full sm:w-80">
              <Select label="Face-matching provider" value={selected}
                disabled={busy || !status.can_manage || status.provider_selectable === false || !((status.allowed_providers ?? ['local','rekognition']).length)}
                onChange={event => { setSelected(event.target.value as MatchingProvider); setSaved(false); }}
              >
                <option value="local" disabled={!!status.allowed_providers && !status.allowed_providers.includes('local')}>Local / offline (Docker)</option>
                <option value="rekognition" disabled={!!status.allowed_providers && !status.allowed_providers.includes('rekognition')}>Amazon Rekognition (AWS)</option>
              </Select>
            </div>
            {status.can_manage && <Button disabled={busy || !status.configured || status.provider_selectable === false || selected === status.provider || (!!status.allowed_providers && !status.allowed_providers.includes(selected))}
              onClick={save} isLoading={busy}>Save face-matching provider</Button>}
          </div>
          <p className="text-sm text-text-secondary">{selected === 'local'
            ? 'Face detection and matching run on your server using the existing local models. No AWS credentials are needed for local matching.'
            : 'Face detection and matching use Amazon Rekognition. The AWS worker and its credentials must be configured on the server.'}</p>
          {selected !== status.provider && <p className="text-xs text-text-muted">Changing provider re-indexes your existing registry photos and resets similarity settings to that provider’s defaults. Photos are kept. Matching checks new events; old events are not replayed as greetings.</p>}
          <p className="text-xs text-text-muted">This choice applies only to this company’s face matching. Storage and event metadata keep their own settings.</p>
          {status.platform_allowed === false && <p className="text-sm text-status-warning">Face processing is currently restricted by the company plan. Your saved configuration is preserved.</p>}
          {!status.configured && <p className="text-sm text-text-muted">Set up the face-matching worker on the server before saving.</p>}
          {status.provider_selectable === false && <p className="text-sm text-text-muted">The server needs the image-AI database migration before providers can be selected.</p>}
          {saved && <p role="status" className="text-sm text-status-success">Face-matching provider saved. Registry photos will be indexed by the selected worker.</p>}
        </>}
        {error && <p role="alert" className="text-sm text-status-danger">{error}</p>}
      </div>
    </div>
  </Card>;
};

export default FaceMatchingProvider;
